import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { mockVocabulary } from './data/mockData';
import { vocabService } from './services/vocabService';
import { useRealTimeSync } from './hooks/useRealTimeSync';
import { BrutalistButton, BrutalistInput } from './components/BrutalistUI';
import GridView from './components/GridView';
import TableView from './components/TableView';
import FlashcardView from './components/FlashcardView';
import CategoryFilter from './components/CategoryFilter';
import DetailModal from './components/DetailModal';
import EditModal from './components/EditModal';
import CreateModal from './components/CreateModal';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';
import './index.css';

function App() {
  const [view, setView] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [editingTerm, setEditingTerm] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [vocabulary, setVocabulary] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [syncError, setSyncError] = useState(null);

  const fetchVocabulary = useCallback(async (isInitial = false) => {
    try {
      if (isInitial) setInitialLoading(true);
      setSyncError(null);
      await vocabService.seedIfEmpty(mockVocabulary);
      const data = await vocabService.getAll();
      setVocabulary(data);
    } catch (error) {
      console.error('Fetch failed:', error);
      setSyncError(error.message);
    } finally {
      if (isInitial) setInitialLoading(false);
    }
  }, []);

  const { isSyncing, lastSynced, forceSync } = useRealTimeSync(fetchVocabulary, 30000);

  useEffect(() => {
    fetchVocabulary(true);
  }, [fetchVocabulary]);

  const handleCreateTerm = async (newData) => {
    try {
      const result = await vocabService.create(newData);
      setVocabulary(prev => [result, ...prev]);
      forceSync();
    } catch (error) {
      console.error('Create failed:', error);
      throw error;
    }
  };

  const handleUpdateTerm = async (updatedData) => {
    try {
      const result = await vocabService.update(updatedData.id, updatedData);
      setVocabulary(prev => prev.map(item => item.id === result.id ? result : item));
      if (selectedTerm?.id === result.id) setSelectedTerm(result);
      forceSync();
    } catch (error) {
      console.error('Update failed:', error);
      throw error;
    }
  };

  const handleDeleteTerm = async (id) => {
    try {
      await vocabService.delete(id);
      setVocabulary(prev => prev.filter(item => item.id !== id));
      if (selectedTerm?.id === id) setSelectedTerm(null);
      forceSync();
    } catch (error) {
      console.error('Delete failed:', error);
      throw error;
    }
  };

  const categories = useMemo(() => {
    const counts = vocabulary.reduce((acc, item) => {
      acc[item.category] = (acc[item.category] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [vocabulary]);

  const filteredData = useMemo(() => {
    return vocabulary.filter(item => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = (
        item.term.toLowerCase().includes(searchLower) ||
        item.plainEnglish.toLowerCase().includes(searchLower) ||
        item.category.toLowerCase().includes(searchLower) ||
        (Array.isArray(item.keyTerms) && item.keyTerms.some(term => term.toLowerCase().includes(searchLower)))
      );
      const matchesCategory = selectedCategory === 'ALL' || item.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, vocabulary, selectedCategory]);

  return (
    <div className="min-h-screen bg-[#F4F0EB] text-black font-sans selection:bg-black selection:text-white pb-20">
      <header className="border-b-4 border-black bg-white sticky top-0 z-40 shadow-[0px_4px_0px_0px_rgba(0,0,0,1)]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-black flex items-center justify-center text-white border-2 border-white shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] relative">
              <SafeIcon icon={FiIcons.FiDatabase} className="text-xl" />
              {isSyncing && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white animate-pulse" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-black uppercase tracking-tighter leading-none">Lexicon.</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className={`w-2 h-2 rounded-full ${isSyncing ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="text-[10px] font-mono font-bold text-gray-400 uppercase">
                  {isSyncing ? 'Syncing...' : `Last Synced: ${format(lastSynced, 'HH:mm:ss')}`}
                </span>
              </div>
            </div>
          </div>

          <div className="flex-grow max-w-xl w-full mx-4">
            <div className="relative">
              <SafeIcon icon={FiIcons.FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-xl text-gray-400" />
              <BrutalistInput 
                placeholder="SEARCH LEXICON..." 
                className="pl-12 !py-2 !text-base" 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
              />
            </div>
          </div>

          <div className="flex gap-2 bg-black p-1 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
            <button 
              onClick={() => setView('grid')} 
              className={`flex items-center gap-2 px-3 py-1.5 text-xs font-black uppercase transition-colors ${view === 'grid' ? 'bg-white text-black' : 'text-white hover:bg-white/20'}`}
            >
              <SafeIcon icon={FiIcons.FiGrid} /> Grid
            </button>
            <button 
              onClick={() => setView('table')} 
              className={`flex items-center gap-2 px-3 py-1.5 text-xs font-black uppercase transition-colors ${view === 'table' ? 'bg-white text-black' : 'text-white hover:bg-white/20'}`}
            >
              <SafeIcon icon={FiIcons.FiList} /> Table
            </button>
            <button 
              onClick={() => setView('flashcards')} 
              className={`flex items-center gap-2 px-3 py-1.5 text-xs font-black uppercase transition-colors ${view === 'flashcards' ? 'bg-white text-black' : 'text-white hover:bg-white/20'}`}
            >
              <SafeIcon icon={FiIcons.FiLayers} /> Cards
            </button>
          </div>
        </div>
      </header>

      {syncError && (
        <div className="max-w-7xl mx-auto px-6 pt-6">
          <div className="bg-[#FFB5A7] border-4 border-black p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] flex items-start gap-3">
            <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-2xl flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-black uppercase tracking-wider mb-1">Database Sync Warning</p>
              <p className="text-sm font-bold opacity-80">{syncError}</p>
              <BrutalistButton onClick={() => fetchVocabulary(true)} className="mt-3 !py-1 !px-3 !text-xs !bg-white">Retry Connection</BrutalistButton>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-6 pt-6">
        {view !== 'flashcards' && (
          <div className="space-y-6 mb-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4 border-2 border-black p-2 px-4 bg-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                <div className="font-mono text-sm font-bold uppercase flex items-center gap-2">
                  <span>Results:</span>
                  <span className="text-xl font-black bg-black text-white px-2 py-0.5">
                    {initialLoading ? '...' : filteredData.length}
                  </span>
                  {isSyncing && <SafeIcon icon={FiIcons.FiRefreshCw} className="animate-spin text-sm" />}
                </div>
              </div>
              <div className="flex gap-4">
                <BrutalistButton onClick={() => forceSync()} className="!bg-white" icon={<SafeIcon icon={FiIcons.FiRefreshCw} className={isSyncing ? 'animate-spin' : ''} />}>Sync</BrutalistButton>
                <BrutalistButton onClick={() => setIsCreateModalOpen(true)} className="!bg-green-400 !text-black" icon={<SafeIcon icon={FiIcons.FiPlus} />}>New Term</BrutalistButton>
              </div>
            </div>
            <CategoryFilter categories={categories} selectedCategory={selectedCategory} onSelectCategory={setSelectedCategory} />
          </div>
        )}

        {initialLoading && vocabulary.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-16 h-16 border-8 border-black border-t-transparent animate-spin"></div>
            <p className="font-black uppercase text-xl">Connecting to Lexicon Database...</p>
          </div>
        ) : (
          <>
            {view === 'grid' && <GridView data={filteredData} onSelectTerm={setSelectedTerm} />}
            {view === 'table' && <TableView data={filteredData} onSelectTerm={setSelectedTerm} />}
            {view === 'flashcards' && <FlashcardView data={filteredData} />}
          </>
        )}
      </main>

      <DetailModal 
        term={selectedTerm} 
        onClose={() => setSelectedTerm(null)} 
        onEdit={(term) => setEditingTerm(term)} 
        onDelete={handleDeleteTerm} 
      />
      <EditModal 
        isOpen={!!editingTerm} 
        term={editingTerm} 
        onClose={() => setEditingTerm(null)} 
        onSave={handleUpdateTerm} 
      />
      <CreateModal 
        isOpen={isCreateModalOpen} 
        onClose={() => setIsCreateModalOpen(false)} 
        onSave={handleCreateTerm} 
      />
    </div>
  );
}

export default App;